import type { AnnotationMLDataT, AnswerT, BaseAnnotationT } from '../../../common/types/types';
import type { CANVAS_OPERATION } from '../constants';
import type { HORIZONTAL_ANCHOR, VERTICAL_ANCHOR } from '../constants/template-builder';
export type AnnotationType = 'RECTANGLE' | 'CUBOID' | 'ELLIPSE' | 'POLYGON' | 'POLYLINE' | 'KEY_POINT' | 'SEGMENTATION' | 'SKELETON' | 'FRAME_CLASSIFICATION';
export type KeyFrameType = 'MANUAL' | 'AUTOMATED';
export type KeyFrameT = {
  isKeyFrame: boolean;
  type?: KeyFrameType;
  source?: string;
};
type ImageLabelingBaseAnnotationT = {
  metadata?: {
    answers?: AnswerT;
    mlData?: AnnotationMLDataT;
    originalId?: string;
    annotationSource?: string;
    consensusInfo?: {
      annotator?: number;
    };
  };
  keyFrame?: KeyFrameT;
} & BaseAnnotationT;
export type BoundingBoxT = {
  x: number;
  y: number;
  width: number;
  height: number;
};
export type RectDataT = {
  rotationAngle?: number;
} & BoundingBoxT;
export type PolygonDataT = {
  points: [number, number][];
};
export type BrushPointT = {
  x: number;
  y: number;
  size: number;
  operation: (typeof CANVAS_OPERATION)[keyof typeof CANVAS_OPERATION];
};
export type BrushDataT = {
  color: string;
  base64String?: string;
  points?: [number, number][]; // deprecated, keeping it to support segmentation V1
};
export type EllipseDataT = {
  centerX: number;
  centerY: number;
  radiusX: number;
  radiusY: number;
};
export type PointDataT = {
  x: number;
  y: number;
};
export type PolylineDataT = {
  points: [number, number][];
};
export type CuboidDataT = {
  points: [[number, number], [number, number], [number, number], [number, number], [number, number], [number, number], [number, number], [number, number]];
};
export type SkeletonDataT = {
  nodes: NodeT[];
  edges: EdgeT[];
  templateId?: string;
};
export type NodeT = {
  point: [number, number];
  id: string;
  description?: string;
  color?: string;
};
export type EdgeT = {
  startNode: string;
  endNode: string;
};
export type RectangleAnnotationT = {
  annotationType: 'RECTANGLE';
  data: RectDataT;
} & ImageLabelingBaseAnnotationT;
export type PolygonAnnotationT = {
  annotationType: 'POLYGON';
  data: PolygonDataT;
} & ImageLabelingBaseAnnotationT;
export type BrushAnnotationT = {
  annotationType: 'SEGMENTATION';
  data: BrushDataT;
} & ImageLabelingBaseAnnotationT;
export type EllipseAnnotationT = {
  annotationType: 'ELLIPSE';
  data: EllipseDataT;
} & ImageLabelingBaseAnnotationT;
export type PointAnnotationT = {
  annotationType: 'KEY_POINT';
  data: PointDataT;
} & ImageLabelingBaseAnnotationT;
export type PolylineAnnotationT = {
  annotationType: 'POLYLINE';
  data: PolylineDataT;
} & ImageLabelingBaseAnnotationT;
export type CuboidAnnotationT = {
  annotationType: 'CUBOID';
  data: CuboidDataT;
} & ImageLabelingBaseAnnotationT;
export type SkeletonAnnotationT = {
  annotationType: 'SKELETON';
  data: SkeletonDataT;
} & ImageLabelingBaseAnnotationT;
export type FrameClassificationT = {
  annotationType: 'FRAME_CLASSIFICATION';
  data: null;
} & ImageLabelingBaseAnnotationT;
export type AnnotationT = RectangleAnnotationT | PolygonAnnotationT | PointAnnotationT | EllipseAnnotationT | PolylineAnnotationT | CuboidAnnotationT | BrushAnnotationT | SkeletonAnnotationT | FrameClassificationT;
export type AnnotationDataT = RectDataT | PolygonDataT | BrushDataT | EllipseDataT | PointDataT | PolylineDataT | CuboidDataT | SkeletonDataT | null;
export type BulkAnnotationT = {
  frameIndex: number;
  annotations: AnnotationWithId[];
};
export type YoloErrorT = {
  frameIndex: number;
  annotationLabel: string;
  message: string;
};
type AnnotationWithId = {
  id: string;
  annotation: AnnotationT;
};
export type SkeletonCanvasT = {
  height: number;
  width: number;
};
export type SkeletonConfigT = {
  canvas: SkeletonCanvasT;
  skeleton: SkeletonDataT;
};
export type SkeletonTemplateNodeT = {
  id: string;
  label: string;
  color?: string;
  x: number;
  y: number;
};
export type SkeletonTemplateEdgeT = {
  sourceNodeId: string;
  targetNodeId: string;
};
export type SkeletonTemplateT = {
  id: string;
  name: string;
  description?: string;
  nodes: SkeletonTemplateNodeT[];
  edges: SkeletonTemplateEdgeT[];
  aspectRatio: number;
};
export type NodeWithPositionT = {
  x: number;
  y: number;
};
export type SkeletonPreviewStyleT = {
  strokeColor: string;
  strokeWidth: number;
};
export type KeypointItemT = {
  id: string;
  label: string;
  color: string;
};
export type VerticalAnchorT = (typeof VERTICAL_ANCHOR)[keyof typeof VERTICAL_ANCHOR];
export type HorizontalAnchorT = (typeof HORIZONTAL_ANCHOR)[keyof typeof HORIZONTAL_ANCHOR];